import type { DayRange } from '../../lib/format/dates';
import { get, type QueryParams } from '../../lib/api/client';
import { fromP1, toP1Query, type P1Response, type Page, type PageRequest } from '../../lib/api/paging';

/**
 * Dashboards + analytics (F4). Contracts from api-core-go:
 *   admin-dashboard-core.go   /admin/dashboard/snapshot, /locales
 *   admin-analytics.go        /admin/analytics/*
 *
 * UNITS: every `*Pct` field is a PERCENT (12.5 = 12.5%), never a fraction.
 * Money is cents. Series buckets are UTC-truncated timestamps.
 */

export type Bucket = 'day' | 'week' | 'month';

/** Shared analytics window (§0.3/§0.4). Dates are YYYY-MM-DD in the admin's profile timezone, inclusive. */
export interface AnalyticsWindow {
  localeIds?: string[];
  startDate?: string;
  endDate?: string;
  bucket?: Bucket;
}

function windowQuery(w: AnalyticsWindow): QueryParams {
  return { localeIds: w.localeIds, startDate: w.startDate, endDate: w.endDate, bucket: w.bucket };
}

// --- dashboard snapshot ----------------------------------------------------------

export interface PlatformSnapshot {
  activeChefs: number;
  /** ALL consumer profiles — chefs included. */
  totalEaters: number;
  dishesListedToday: number;
  dishesListedThisWeek: number;
  /** Portions on completed orders. */
  dishesSoldToday: number;
  dishesSoldThisWeek: number;
  newChefSignups24h: number;
  newChefSignups7d: number;
  newEaterSignups24h: number;
  newEaterSignups7d: number;
  /** Over the requested window (startDate–endDate; trailing 7 days when none is sent). */
  newChefSignups: number;
  newEaterSignups: number;
  dishesListed: number;
  /** Portions on completed orders. */
  dishesSold: number;
  asOf: string;
}

export async function getSnapshot(localeIds: string[], days: DayRange, signal?: AbortSignal) {
  return get<{ snapshot: PlatformSnapshot }>(
    '/admin/dashboard/snapshot',
    { localeIds, startDate: days.start, endDate: days.end },
    signal,
  );
}

// --- community activation ----------------------------------------------------------

export type ActivityLevel = 'high' | 'medium' | 'low';
export type SupplyDemandFlag = 'chef_shortage' | 'balanced' | 'eater_shortage';
export type LocaleSortKey = 'name' | 'chefCount' | 'eaterCount' | 'dishesListed' | 'dishesSold';

export interface LocaleActivityRow {
  localeId: string;
  name: string;
  type: string;
  city: string;
  stateProvince: string;
  chefCount: number;
  eaterCount: number;
  /** Trailing seven days. */
  dishesListed: number;
  dishesSold: number;
  activityLevel: ActivityLevel;
  /** Percent of the weekly sold target. */
  activityPct: number;
  dishesSoldTargetPerWeek: number;
  /** Eaters per ACTIVE chef; null when there are no active chefs. */
  supplyDemandRatio: number | null;
  supplyDemandFlag: SupplyDemandFlag;
  suggestedAction: 'recruit_chefs' | 'recruit_eaters' | null;
  lat?: number;
  lon?: number;
  /** Metres. Circle-defined (incl. postal-code) locales. */
  radius?: number;
  /** Boundary as [[lon, lat], ...] (outer ring) for polygon-defined locales. */
  polygon?: Array<[number, number]>;
}

export interface LocaleActivityParams extends PageRequest {
  /** Free text, ILIKE — there is no Region model. */
  region?: string;
  localeIds?: string[];
  sortBy?: LocaleSortKey;
  order?: 'asc' | 'desc';
  /** One locale type; omitted = every type. */
  type?: LocaleTypeFilter;
  /** Inclusive days for chefs, listed and sold (trailing 7 days when omitted); eaters are all-time. */
  startDate?: string;
  endDate?: string;
}

/** The locale types the Community Activation table can show. */
export type LocaleTypeFilter = 'dish' | 'postalCode';

export async function listLocaleActivity(
  { page, pageSize, ...rest }: LocaleActivityParams,
  signal?: AbortSignal,
): Promise<Page<LocaleActivityRow>> {
  const res = await get<P1Response<LocaleActivityRow>>(
    '/admin/dashboard/locales',
    { ...rest, order: rest.sortBy ? rest.order : undefined, ...toP1Query({ page, pageSize }) },
    signal,
  );
  return fromP1(res);
}

// --- growth series -------------------------------------------------------------------

export interface SeriesPoint {
  /** UTC bucket start. */
  bucket: string;
  count: number;
}

export interface TimeSeries {
  points: SeriesPoint[];
  total: number;
  /** Same-length window immediately before. */
  previousTotal: number;
  /** Percent; null when the previous window was zero. */
  changePct: number | null;
}

export interface SeriesResponse {
  bucket: Bucket;
  series: TimeSeries;
}

export async function getSignupSeries(type: 'chef' | 'eater', w: AnalyticsWindow, signal?: AbortSignal) {
  return get<SeriesResponse & { type: string }>('/admin/analytics/growth/signups', { type, ...windowQuery(w) }, signal);
}

export async function getDishSeries(event: 'listed' | 'sold', w: AnalyticsWindow, signal?: AbortSignal) {
  return get<SeriesResponse & { event: string }>(
    '/admin/analytics/growth/dishes',
    { event, ...windowQuery(w) },
    signal,
  );
}

// --- funnel & cohorts ------------------------------------------------------------------

export interface FunnelStage {
  /** `visits` counts browsers (devices + signed-in profiles); later stages count profiles that signed up. */
  stage: 'visits' | 'signup' | 'viewed_listing' | 'first_order_or_publish' | 'repeat' | string;
  count: number;
  /** Percent drop from the previous stage; null on the first. */
  dropOffPctFromPrev: number | null;
}

export async function getFunnel(w: AnalyticsWindow, signal?: AbortSignal) {
  return get<{ stages: FunnelStage[]; missingStages?: string[] }>(
    '/admin/analytics/growth/funnel',
    windowQuery(w),
    signal,
  );
}

export interface CohortRow {
  cohortStart: string;
  size: number;
  day7Active: number;
  day30Active: number;
  day90Active: number;
}

export async function getCohortRetention(
  type: 'chef' | 'eater',
  cohortBucket: Bucket,
  w: AnalyticsWindow,
  signal?: AbortSignal,
) {
  return get<{ cohorts: CohortRow[] | null }>(
    '/admin/analytics/growth/cohort-retention',
    { type, cohortBucket, ...windowQuery(w) },
    signal,
  );
}

// --- chef ---------------------------------------------------------------------------------

export interface ChefActivityTrends {
  weeksMeasured: number;
  activeChefs: number;
  inactiveChefs: number;
  goneChefs: number;
  avgDishesPerChefPerWeek: number;
  avgSoldPerChefPerWeek: number;
  activeWithinDays: number;
  goneAfterDays: number;
}

export async function getChefActivityTrends(w: AnalyticsWindow, weeks = 4, signal?: AbortSignal) {
  return get<{ trends: ChefActivityTrends }>(
    '/admin/analytics/chef/activity-trends',
    { ...windowQuery(w), weeks },
    signal,
  );
}

export interface TopPerformer {
  profileId: string;
  displayName: string;
  localeId?: string;
  dishesPublished: number;
  dishesOrdered: number;
  /** Percent. */
  orderRatePct: number;
  windowDays: number;
}

export async function getTopPerformers(w: AnalyticsWindow, limit = 10, signal?: AbortSignal) {
  return get<{ items: TopPerformer[] | null }>(
    '/admin/analytics/chef/top-performers',
    { ...windowQuery(w), limit },
    signal,
  );
}

// --- eater -------------------------------------------------------------------------------------

export interface RepeatPurchaseStats {
  windowDays: number;
  eatersWithOrders: number;
  repeatPurchasers: number;
  /** Percent. */
  repeatPurchasePct: number;
}

export async function getRepeatPurchaseRate(w: AnalyticsWindow, signal?: AbortSignal) {
  return get<{ stats: RepeatPurchaseStats }>('/admin/analytics/eater/repeat-purchase-rate', windowQuery(w), signal);
}

export interface AvgOrderValue {
  transactions: number;
  /** Eater's view: food + tax + fees. */
  avgChargeTotalCents: number;
  /** Chef's view: food alone. */
  avgGrossCents: number;
  avgTaxCents: number;
  totalChargedCents: number;
}

export async function getAvgOrderValue(w: AnalyticsWindow, signal?: AbortSignal) {
  return get<{ avgOrderValue: AvgOrderValue }>('/admin/analytics/eater/avg-order-value', windowQuery(w), signal);
}

export interface CuisineOrdered {
  tag: string;
  tagType: string;
  listings: number;
  portions: number;
}

export async function getTopOrderedCuisines(w: AnalyticsWindow, limit = 8, signal?: AbortSignal) {
  return get<{ items: CuisineOrdered[] | null }>(
    '/admin/analytics/eater/top-ordered-cuisines',
    { ...windowQuery(w), limit },
    signal,
  );
}

export interface ViewedDish {
  listingId: string;
  title: string;
  listerId: string;
  listerName: string;
  localeId?: string;
  /** Identified people. */
  uniqueViewers: number;
  /** Signed-out volume (opens). */
  anonymousOpens: number;
  /** Signed-out breadth: distinct devices (browsers), not people. */
  anonymousViewers: number;
  totalViews: number;
  lastViewedAt: string;
  ordersPlaced: number;
  /** Percent. */
  viewToOrderPct: number;
}

export async function getMostViewedDishes(w: AnalyticsWindow, limit = 8, signal?: AbortSignal) {
  return get<{ items: ViewedDish[] | null }>(
    '/admin/analytics/eater/most-viewed-dishes',
    { ...windowQuery(w), limit },
    signal,
  );
}

export interface ViewedCuisine {
  tag: string;
  uniqueViewers: number;
  anonymousOpens: number;
  /** Signed-out breadth: distinct devices (browsers), not people. */
  anonymousViewers: number;
  totalViews: number;
  listings: number;
  portionsSold: number;
  /** Percent. */
  viewToOrderPct: number;
}

export async function getMostViewedCuisines(w: AnalyticsWindow, limit = 8, signal?: AbortSignal) {
  return get<{ items: ViewedCuisine[] | null }>(
    '/admin/analytics/eater/most-viewed-cuisines',
    { ...windowQuery(w), limit },
    signal,
  );
}

/**
 * Eater-behaviour outcomes over the range. Percents are null when there is
 * nothing to divide by.
 *   listings*  — listings whose window CLOSED in the range (dated at close).
 *   eaterDays* — signed-in eater-days with a view (browse without order).
 *   orders*    — orders CREATED in the range.
 */
export interface ListingOutcomes {
  listingsClosed: number;
  listingsViewed: number;
  /** Closed listings that were viewed (by anyone) and got no order. */
  listingsViewedNotOrdered: number;
  /** Percent of listingsViewed. */
  viewedNotOrderedPct: number | null;
  /** Signed-in (eater, day) pairs with at least one view. */
  eaterDaysBrowsed: number;
  /** Of those, days the eater placed no order. */
  eaterDaysWithoutOrder: number;
  eatersWithoutOrder: number;
  /** Percent of eaterDaysBrowsed. */
  browseWithoutOrderPct: number | null;
  /** Orders past held (checkout started). */
  ordersStarted: number;
  ordersAbandoned: number;
  /** Percent of ordersStarted. */
  abandonedPct: number | null;
}

export async function getListingOutcomes(w: AnalyticsWindow, signal?: AbortSignal) {
  return get<{ outcomes: ListingOutcomes }>('/admin/analytics/eater/browse-without-order', windowQuery(w), signal);
}
